import type { DetailResponse } from './get-address-fn';
import type { GoogleAddressResponse } from './get-google-address-fn';
import type { Address, Feature, SavedPlace } from './types';

export const googleAddressResponseToAddress = (
  params: GoogleAddressResponse
): Address | null => {
  try {
    if (params?.formatted_address && params.geometry?.location) {
      const [firstLabel, secondLabel] = getAddressLabels(
        params.formatted_address
      );

      return {
        first_label: params.name ?? firstLabel,
        second_label: params.name ? params.formatted_address : secondLabel,
        address: params.formatted_address,
        lat: params.geometry.location.lat,
        lng: params.geometry.location.lng,
        short_address: params.name ?? firstLabel,
      };
    }
    return null;
  } catch (e) {
    return null;
  }
};

export const detailResponseToAddress = (params: DetailResponse): Address => {
  try {
    if (params?.formatted_address && params.geometry?.location) {
      const addressMaterial = params.formatted_address.split(',');
      const first_label = addressMaterial[0] || params.formatted_address;
      const second_label =
        addressMaterial.slice(1).join(', ') || params.formatted_address;

      return {
        first_label,
        second_label,
        address: params.formatted_address,
        lat: params.geometry.location.lat,
        lng: params.geometry.location.lng,
        short_address: first_label,
        entrances: [],
      };
    }

    return {} as Address;
  } catch {
    return {} as Address;
  }
};

export const getAddressLabels = (address: string): string[] => {
  if (!address) return [''];
  const firstLabel = address.split(',')[0]!;

  const start = firstLabel.length;
  const length = address.length;

  const secondLabel = address?.substring(start, length).replace(',', '').trim();

  return [firstLabel, secondLabel];
};

export const formatFeatureToAddress = (feature: Feature): Address | null => {
  if (feature.properties) {
    const address = feature.properties.name;

    const [firstLabel, secondLabel] = getAddressLabels(address);

    const payload: Address = {
      address: feature.properties.name,
      description: feature?.properties?.name,
      short_address: feature.properties.name,
      place_id: feature.properties.place_id,
      adr_source: feature.properties.source || '',
      first_label: firstLabel,
      second_label: secondLabel,
      entrances: feature.properties.entrances,
      polygon: feature.properties.polygon,
    };

    if (feature.type) {
      payload.types = [feature.type];
    }

    if (feature.geometry?.coordinates) {
      payload.geometry = feature.geometry;

      payload.lat = feature.geometry.coordinates[1];
      payload.lng = feature.geometry.coordinates[0];
    }
    if (feature.code) {
      payload.code = feature.code;
    }
    if (feature.feature_type) {
      payload.feature_type = feature.feature_type;
    }

    return payload;
  }

  return null;
};

export const formatSavedPlaceToAddress = (saved: SavedPlace): Address => {
  const address = saved.address!;
  const location = saved.location;
  const [firstLabel, secondLabel] = getAddressLabels(address);

  const payload: Address = {
    ...saved,
    address,
    description: saved.name,
    short_address: saved.name,
    first_label: firstLabel,
    second_label: secondLabel,
  };

  if (location?.coordinates) {
    payload.lat = location.coordinates[1];
    payload.lng = location.coordinates[0];
  }

  if (location?.type) {
    payload.types = [location.type];
  }

  return payload;
};
